import type { UserType } from "@/domain/core";

/**
 * Maps UserType to their corresponding dashboard routes
 */
export const USER_TYPE_ROUTES: Record<UserType, string> = {
  admin: "/admin",
  support: "/admin", // Support shares the admin console, minus sensitive actions
  professional: "/professional",
  practice: "/practice",
  none: "/"
};

/**
 * UserType priority for redirection (higher priority first)
 */
const USER_TYPE_PRIORITY: UserType[] = [
  "admin",
  "support",
  "professional",
  "practice",
  "none"
];

/**
 * Determines the appropriate redirect route based on user type
 * @param userType - The user type from API response
 * @returns The route to redirect to, or null if no valid user type found
 */
export function getUserTypeBasedRedirect(userType: UserType): string | null {
  // Validate input
  if (!userType) {
    return null;
  }

  // Handle "none" user type - return root route
  if (userType === "none") {
    return "/";
  }

  // Find the user type in priority order
  for (const type of USER_TYPE_PRIORITY) {
    if (userType === type) {
      const route = USER_TYPE_ROUTES[type];

      // Validate that we have a route for this user type
      if (route) {
        return route;
      }
    }
  }

  // If no valid user type found, return null
  return null;
}

/**
 * Gets the default route for authenticated users without specific user type
 * @returns The default route
 */
export function getDefaultAuthenticatedRoute(): string {
  return "/";
}

/**
 * Checks if a user has any of the required user types
 * @param requiredTypes - Array of required user types
 * @param userType - User's actual type from API
 * @returns True if user has any of the required types, false otherwise
 */
export function hasAnyUserType(
  requiredTypes: UserType[],
  userType: UserType
): boolean {
  if (!userType || userType === "none") {
    return false;
  }

  return requiredTypes.includes(userType);
}
